import React from 'react';
import styled from "@emotion/styled";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTheme } from "@mui/styles";
import CircleIcon from '@mui/icons-material/Circle';
import CircularProgress from "@mui/material/CircularProgress";

const AnalyticsPieCard = ({ title, pieChartData, colors, legendItems, emptyText = "no closures", loading = false }) => {
  const theme = useTheme()
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
    const radius = outerRadius + 8;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text x={x} y={y} fill={theme.palette.common.black} fontSize={11} fontWeight={850} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const noActivity = pieChartData && pieChartData.every((entry) => entry.size === 0)

  return (
    <PieCard>
      <CardTitle>{title}</CardTitle>
      <ChartFrame>
        {loading ?
          <CircularProgress
            sx={{ color: `${theme.palette.primary.main}`}}
            size={18}
          />
        : noActivity ?
          <EmptyState>{emptyText}</EmptyState>
        :
          <ResponsiveContainer width="100%" height="100%" minHeight={82}>
            <PieChart width={400} height={300}>
              <Pie
                label={renderCustomizedLabel}
                dataKey="size"
                startAngle={180}
                endAngle={0}
                data={pieChartData}
                cx="50%"
                cy="82%"
                outerRadius="76%"
                innerRadius="26%"
                fill="#8884d8"
                labelLine={false}
                animationDuration={500}
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} stroke={theme.palette.common.white} strokeWidth={2} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip title={title} />} cursor={{fill: `rgba(147, 149, 153, 0.1)`}} />
            </PieChart>
          </ResponsiveContainer>
        }
      </ChartFrame>
      <LegendRows>
        {legendItems.map((item) => (
          <LegendRow key={item.label}>
            <LegendLabel>
              <CircleIcon style={{color: item.color}} />
              <span>{item.label}</span>
            </LegendLabel>
            <LegendValue>{item.value}</LegendValue>
          </LegendRow>
        ))}
      </LegendRows>
    </PieCard>
  )
}

const CustomTooltip = ({ active, payload, title }) => {
  if (active && payload && payload.length) {
    return (
      <TooltipSurface>
        <TooltipHeader>{title}</TooltipHeader>
        <TooltipRow>
          <TooltipLabel>{payload[0].name || payload[0].payload?.name}</TooltipLabel>
          <TooltipValue>{payload[0].value}</TooltipValue>
        </TooltipRow>
      </TooltipSurface>
    )
  }

  return null
}

const PieCard = styled.article(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(5rem, 1fr) auto;
  gap: 0.28rem;
  padding: 0.42rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.16rem 0.5rem rgba(15, 23, 42, 0.035);
`)

const CardTitle = styled.h3(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
  text-align: center;
`)

const ChartFrame = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 5rem;
  display: grid;
  place-items: center;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.52rem;
  background: ${theme.palette.common.white};
  overflow: hidden;
`)

const EmptyState = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 800;
  line-height: 1.2;
`)

const LegendRows = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.22rem;
`

const LegendRow = styled.div(({ theme }) => `
  min-width: min(100%, 6.85rem);
  flex: 1 1 6.85rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(max-content, auto);
  gap: 0.18rem;
  align-items: center;
  padding: 0.2rem 0.26rem;
  border: 0.1rem solid ${theme.palette.common.grayBorderLight};
  border-radius: 0.45rem;
  background: ${theme.palette.common.white};

  &:first-of-type {
    flex-grow: 1.25;
  }
`)

const LegendLabel = styled.div(({ theme }) => `
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.16rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.58rem;
  font-weight: 750;
  line-height: 1.2;
  overflow: hidden;
  white-space: nowrap;

  svg {
    width: 0.52rem;
    height: 0.52rem;
    flex: 0 0 auto;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`)

const LegendValue = styled.div(({ theme }) => `
  min-width: max-content;
  color: ${theme.palette.common.black};
  font-size: 0.62rem;
  font-weight: 850;
  line-height: 1;
  text-align: right;
  white-space: nowrap;
`)

const TooltipSurface = styled.div(({ theme }) => `
  width: 10.5rem;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.62rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.7rem 1.4rem rgba(15, 23, 42, 0.14);
`)

const TooltipHeader = styled.div(({ theme }) => `
  padding: 0.5rem 0.6rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
`)

const TooltipRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
`

const TooltipLabel = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 750;
  line-height: 1.2;
`)

const TooltipValue = styled.span(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 850;
  line-height: 1.2;
`)

export default AnalyticsPieCard
